import type { FolderSchema } from '../api/contracts'
import type { FolderNode } from '../models'

/**
 * Convert a flat array of FolderSchema (as stored on the backend) into a
 * nested tree of FolderNode — sorted at every level by sort_order, and
 * with tables inside each folder sorted by their own sort_order.
 *
 * Folders whose parent_id references a non-existent folder are dropped
 * (defensive — shouldn't happen if the backend validates).
 */
export function buildTree(folders: FolderSchema[]): FolderNode[] {
  const map = new Map<string, FolderNode>()
  for (const f of folders) {
    map.set(f.id, { ...f, tables: [...f.tables], children: [] })
  }

  const roots: FolderNode[] = []
  for (const node of map.values()) {
    // Sort tables within each folder
    node.tables.sort((a, b) => a.sort_order - b.sort_order)

    if (node.parent_id === null) {
      roots.push(node)
    } else {
      const parent = map.get(node.parent_id)
      if (parent) parent.children.push(node)
      // else: orphan — drop silently
    }
  }

  // Recursively sort children by sort_order
  const sortRecursive = (nodes: FolderNode[]): void => {
    nodes.sort((a, b) => a.sort_order - b.sort_order)
    for (const n of nodes) sortRecursive(n.children)
  }
  sortRecursive(roots)

  return roots
}

/**
 * Collect every table_id referenced anywhere in the folder tree.
 * Used to determine which tables are "unorganized" (show outside the tree).
 */
export function collectOrganizedTableIds(folders: FolderSchema[]): Set<string> {
  const ids = new Set<string>()
  for (const f of folders) {
    for (const t of f.tables) ids.add(t.table_id)
  }
  return ids
}

/**
 * Recursive table count for a folder — includes tables in this folder and
 * in all descendant folders. Used for the count badge.
 */
export function countTablesRecursive(node: FolderNode): number {
  let count = node.tables.length
  for (const child of node.children) {
    count += countTablesRecursive(child)
  }
  return count
}
